import { Controller } from "stimulus";

export default class ThreadImagesController extends Controller {
  connect() {
    this.files = [];
    this.fileKeys = new Set();
    this.ALLOWED_TYPES = new Set(["image/jpeg", "image/png", "image/gif", "image/pjpeg", "image/x-png"]);
    this.MAX_SIZE = 5 * 1024 * 1024; // 5MB to match uploader
  }

  clickInput() {
    if (this.inputTarget) this.inputTarget.click();
  }

  async filesChanged() {
    const incoming = Array.from(this.inputTarget.files || []);
    this.clearErrors();
    await Promise.all(incoming.map((file) => this.tryAddFile(file)));

    // Reset native input so selecting the same file again still triggers change
    this.inputTarget.value = "";

    this.syncInputFiles();
    this.renderPreviews();
  }

  clearPreviews() {
    this.previewsTarget.innerHTML = "";
  }

  showError(message) {
    if (!this.hasErrorsTarget) return;
    const item = document.createElement("div");
    item.className = "mt-1 text-red-600";
    item.textContent = message;
    this.errorsTarget.appendChild(item);
  }

  clearErrors() {
    if (this.hasErrorsTarget) this.errorsTarget.innerHTML = "";
  }

  async tryAddFile(file) {
    if (!file) return false;

    // Type check
    const type = file.type || "";
    if (!this.ALLOWED_TYPES.has(type)) {
      this.showError(`${file.name}: unsupported file type`);
      return false;
    }

    // Size check
    if (file.size > this.MAX_SIZE) {
      this.showError(`${file.name}: file is larger than 5MB`);
      return false;
    }

    const key = [file.name, file.size, file.lastModified].join(":");
    if (this.fileKeys.has(key)) return false;
    this.files.push(file);
    this.fileKeys.add(key);
    return true;
  }

  removeFile(event) {
    const index = parseInt(event.currentTarget?.dataset?.index || -1, 10);
    if (isNaN(index) || index < 0 || index >= this.files.length) return;
    const [removed] = this.files.splice(index, 1);
    if (removed) {
      const key = [removed.name, removed.size, removed.lastModified].join(":");
      this.fileKeys.delete(key);
    }
    this.syncInputFiles();
    this.renderPreviews();
  }

  syncInputFiles() {
    // Combine current files into a FileList using DataTransfer
    const dt = new DataTransfer();
    this.files.forEach((f) => dt.items.add(f));
    this.inputTarget.files = dt.files;
  }

  renderPreviews() {
    this.clearPreviews();
    this.files.forEach((file, index) => {
      const url = URL.createObjectURL(file);
      const img = document.createElement("img");
      img.src = url;
      img.alt = file.name || "Selected image";
      img.className = "w-full h-20 object-cover";
      img.onload = () => URL.revokeObjectURL(url);

      const removeBtn = document.createElement("button");
      removeBtn.type = "button";
      removeBtn.textContent = "✕";
      removeBtn.setAttribute("aria-label", "Remove image");
      removeBtn.dataset.index = String(index);
      removeBtn.className = "absolute top-1 right-1 inline-flex items-center justify-center w-6 h-6 rounded-full bg-white/90 text-gray-700 border border-gray-300 hover:bg-white";
      removeBtn.addEventListener("click", this.removeFile.bind(this));

      const wrap = document.createElement("div");
      wrap.className = "relative overflow-hidden rounded-md border border-gray-200";
      wrap.appendChild(img);
      wrap.appendChild(removeBtn);

      this.previewsTarget.appendChild(wrap);
    });
  }
}

ThreadImagesController.targets = ["input", "previews", "errors"]; 
